const HOURS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];
const MINUTES = ["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"];

function parseTimeTo12H(value: string): { hour: string; minute: string; period: "AM" | "PM" } {
  if (!value) return { hour: "", minute: "00", period: "AM" };
  const [hStr, mStr] = value.split(":");
  const h24 = parseInt(hStr, 10);
  const period: "AM" | "PM" = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return { hour: String(h12), minute: mStr || "00", period };
}

function to24H(hour: string, minute: string, period: "AM" | "PM"): string {
  if (!hour) return "";
  const h12 = parseInt(hour, 10);
  let h24 = h12;
  if (period === "AM" && h12 === 12) h24 = 0;
  else if (period === "PM" && h12 !== 12) h24 = h12 + 12;
  return `${String(h24).padStart(2, "0")}:${minute}`;
}

interface TimePickerProps {
  value: string; // HH:MM in 24H format, or empty string
  onChange: (val: string) => void;
  compact?: boolean; // smaller padding for inline/edit contexts
  /**
   * Distinguishes the test ids of two pickers rendered side by side (start and
   * end time). Without it both instances share one id and tests can only tell
   * them apart by array position, which silently re-targets the moment the
   * markup order changes. Defaults to the original un-prefixed ids so existing
   * single-picker callers and their tests are unaffected.
   */
  testIdPrefix?: string;
}

export function TimePicker({ value, onChange, compact = false, testIdPrefix }: TimePickerProps) {
  const { hour, minute, period } = parseTimeTo12H(value);
  const padClass = compact ? "px-1.5 py-1" : "px-2 py-2";
  const testId = (suffix: string) =>
    testIdPrefix ? `${testIdPrefix}-time-picker-${suffix}` : `time-picker-${suffix}`;

  return (
    <div className="mwbi-time-picker flex items-center gap-1" data-testid={testId("root")}>
      <select
        value={hour}
        onChange={(e) => onChange(to24H(e.target.value, minute, period))}
        data-testid={testId("hour-input")}
        className={`flex-1 min-w-0 ${padClass} border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary`}
      >
        <option value="">--</option>
        {HOURS.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-foreground-muted text-sm shrink-0">:</span>
      <select
        value={minute}
        onChange={(e) => onChange(to24H(hour, e.target.value, period))}
        data-testid={testId("minute-input")}
        className={`flex-1 min-w-0 ${padClass} border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary`}
      >
        {MINUTES.map((m) => (
          <option key={m} value={m}>{m}</option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => {
          const newPeriod = period === "AM" ? "PM" : "AM";
          if (hour) onChange(to24H(hour, minute, newPeriod));
        }}
        data-testid={testId("meridiem-select")}
        className={`${padClass} border border-border rounded-md bg-background text-foreground text-sm hover:bg-sidebar-hover transition whitespace-nowrap`}
      >
        {period}
      </button>
    </div>
  );
}
